// ────────────────────────────────────────────────────────────────────────────
//  useSickLeaves.ts — Hook + DTOs für Krankmeldungen
//
//  Endpoints (gated über Wrangler-Var SICK_LEAVES_ENABLED):
//    POST   /sick-leaves                       — Krankmeldung einreichen
//    POST   /sick-leaves/:id/upload            — Anhang (multipart)
//    GET    /sick-leaves/inbox?status=…        — Manager-Inbox
//    GET    /sick-leaves/mine                  — eigene Krankmeldungen
//    POST   /sick-leaves/:id/approve           — Manager: bestätigen
//    POST   /sick-leaves/:id/needs-followup    — Manager: Rückfrage
//    POST   /sick-leaves/:id/reject            — Manager: ablehnen
//    POST   /sick-leaves/:id/file-to-record    — In Akte übernehmen
//    GET    /sick-leaves/:id/attachment        — Auth-protected Download
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from 'react'
import { api, API_ORIGIN } from './api'

export type SickLeaveStatus =
  | 'pending'
  | 'approved'
  | 'needs_followup'
  | 'rejected'
  | 'filed'

export interface SickLeave {
  id:                  number
  orgId:               number
  userId:              string
  submitterName:       string | null
  startsOn:            string | null     // YYYY-MM-DD
  endsOn:              string | null
  reason:              string | null
  attachmentUrl:       string | null     // /api/v1/sick-leaves/:id/attachment
  attachmentName:      string | null
  attachmentType:      string | null
  attachmentSizeBytes: number
  status:              SickLeaveStatus
  decisionNote:        string | null
  decidedBy:           string | null
  decidedByName:       string | null
  decidedAt:           string | null
  filedToFileId:       number | null
  filedAt:             string | null
  createdAt:           string
}

// ── Hooks ────────────────────────────────────────────────────────────────

export function useMySickLeaves() {
  const [items,   setItems]   = useState<SickLeave[]>([])
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const list = await api.get<SickLeave[]>('/sick-leaves/mine')
      setItems(list)
    } catch (e: any) {
      setError(e?.message ?? 'Krankmeldungen konnten nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { reload() }, [reload])
  return { items, loading, error, reload }
}

export function useSickLeaveInbox(filterStatus: string = 'pending') {
  const [items,   setItems]   = useState<SickLeave[]>([])
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const list = await api.get<SickLeave[]>(
        `/sick-leaves/inbox?status=${encodeURIComponent(filterStatus)}`
      )
      setItems(list)
    } catch (e: any) {
      setError(e?.message ?? 'Inbox konnte nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [filterStatus])

  useEffect(() => { reload() }, [reload])
  return { items, loading, error, reload }
}

// ── Actions ──────────────────────────────────────────────────────────────

export async function submitSickLeave(input: {
  startsOn?: string | null
  endsOn?:   string | null
  reason?:   string
}): Promise<SickLeave> {
  return api.post<SickLeave>('/sick-leaves', input)
}

export async function uploadSickLeaveAttachment(
  id: number, file: File,
): Promise<SickLeave> {
  const token = localStorage.getItem('custosoft.auth.token') ?? ''
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${API_ORIGIN}/api/v1/sick-leaves/${id}/upload`, {
    method:  'POST',
    headers: { Authorization: `Bearer ${token}` },
    body:    fd,
  })
  const text = await res.text()
  let payload: any = {}
  try { payload = text ? JSON.parse(text) : {} } catch { /* */ }
  if (!res.ok) throw new Error(payload?.error ?? `HTTP ${res.status}`)
  return payload as SickLeave
}

export async function approveSickLeave(id: number): Promise<SickLeave> {
  return api.post<SickLeave>(`/sick-leaves/${id}/approve`, {})
}
export async function followupSickLeave(id: number, note: string): Promise<SickLeave> {
  return api.post<SickLeave>(`/sick-leaves/${id}/needs-followup`, { note })
}
export async function rejectSickLeave(id: number, note: string): Promise<SickLeave> {
  return api.post<SickLeave>(`/sick-leaves/${id}/reject`, { note })
}
export async function fileSickLeaveToRecord(id: number): Promise<{ ok: boolean; fileId?: number }> {
  return api.post<{ ok: boolean; fileId?: number }>(`/sick-leaves/${id}/file-to-record`, {})
}

// ── Helpers ──────────────────────────────────────────────────────────────

export const STATUS_META: Record<SickLeaveStatus, {
  label: string; color: string; bg: string; icon: string
}> = {
  pending:        { label: 'Wartend',          color: '#ffb300', bg: 'rgba(255,179,0,0.16)',  icon: '⏳' },
  approved:       { label: 'Bestätigt',        color: '#33d670', bg: 'rgba(51,214,112,0.16)', icon: '✓' },
  needs_followup: { label: 'Rückfrage',        color: '#3a62ff', bg: 'rgba(58,98,255,0.16)',  icon: '?' },
  rejected:       { label: 'Abgelehnt',        color: '#e8595d', bg: 'rgba(232,89,93,0.16)',  icon: '✕' },
  filed:          { label: 'In Akte übernommen', color: '#8a44ee', bg: 'rgba(138,68,238,0.16)', icon: '📁' },
}
